import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DetailSection,
  DateField,
  DetailSections,
  Fact,
  Facts,
  Field,
  KanbanTag,
  Modal,
  ModalActions,
  MultiSelect,
  Page,
  PageHeader,
  Select,
  TextArea,
  backLinkClasses,
  linkTextClasses,
} from '@evogard/ui';
import {
  RETENTION_PHASE_LABELS,
  format,
  formatDate,
  formatDocument,
  formatInstant,
  formatPhone,
  fromColumn,
  loadFailureMessage,
  type RetentionPhase,
} from '@evogard/domain';
import type { RetentionCardDetail } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { RETENTION_KEY, useInvalidateRetention } from './useRetention.js';

const FINALS = [
  { phase: 'retido', label: 'Retido' },
  { phase: 'cancelado', label: 'Cancelado' },
  { phase: 'divergencia_documentacao', label: 'Divergência de documentação' },
] as const;

/** O valor do banco como dinheiro na tela — vazio vira travessão. */
function moneyText(column: string | null): string {
  const m = column === null ? null : fromColumn(column);
  return m === null ? '—' : format(m);
}

// @valor
const ATTACHMENT_LABELS: Record<string, string> = {
  selfie_documento: 'Selfie segurando o documento',
  documento_aberto: 'Documento aberto (frente e verso)',
  boleto_pdf: 'Boleto (do sistema antigo)',
};

/**
 * O cartão aberto — o `CardModal` do legado, numa página.
 *
 * A pessoa e o veículo vêm do cadastro, agora; o que a pessoa digitou no
 * pedido aparece ao lado quando difere. O que cada um pode fazer vem do
 * servidor (`can`), e a tela só mostra o botão.
 */
export function RequestScreen() {
  const id = Number((useParams({ strict: false }) as { id?: string }).id);
  const search = useSearch({ strict: false }) as { tenant?: string };
  const { data: context } = useSessionContext();
  const tenant = search.tenant ?? context?.currentTenant.slug ?? '';
  const invalidate = useInvalidateRetention();

  const detail = useQuery({
    queryKey: [...RETENTION_KEY, 'cartao', tenant, id],
    enabled: tenant !== '',
    queryFn: async () => {
      const r = await api.retention.detail({ params: { tenant, id } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o pedido'));
      return r.body;
    },
  });

  const d = detail.data;
  return (
    <Page>
      <Link to="/retention/board" className={backLinkClasses()}>
        Voltar ao Kanban Cancelamento
      </Link>
      {detail.isError && <Alert tone="erro">{detail.error.message}</Alert>}
      {d && (
        <>
          <PageHeader
            title={`${d.card.memberName ?? 'Associado'} · ${d.card.plate ?? 'sem placa'}`}
          >
            Protocolo {d.card.protocol} · {tenant.toUpperCase()} ·{' '}
            {RETENTION_PHASE_LABELS[d.card.phase as RetentionPhase]}
            {d.card.operator ? ` · com ${d.card.operator.name}` : ''}
          </PageHeader>
          <Actions tenant={tenant} detail={d} onChanged={invalidate} />
          <DetailSections>
            <DetailSection title="Associado" icon="pessoa">
              <Facts>
                <Fact label="Nome">{d.member.name ?? '—'}</Fact>
                <Fact label="CPF">
                  {d.member.document ? formatDocument(d.member.document) : '—'}
                </Fact>
                <Fact label="E-mail do cadastro">{d.member.email ?? '—'}</Fact>
                <Fact label="Telefone do cadastro">
                  {d.member.phone ? formatPhone(d.member.phone) : '—'}
                </Fact>
                {d.member.requestEmail && (
                  <Fact label="E-mail informado no pedido">{d.member.requestEmail}</Fact>
                )}
                {d.member.requestPhone && (
                  <Fact label="Telefone informado no pedido">
                    {formatPhone(d.member.requestPhone)}
                  </Fact>
                )}
                <Fact label="Motivo" wide>
                  {d.card.reasonText ?? '—'}
                </Fact>
              </Facts>
            </DetailSection>

            <DetailSection title="Veículo" icon="veiculo">
              <Facts>
                <Fact label="Placa">{d.vehicle.plate ?? 'Sem placa'}</Fact>
                <Fact label="Modelo">{d.vehicle.model ?? '—'}</Fact>
                <Fact label="Chassi">{d.vehicle.chassis ?? '—'}</Fact>
                <Fact label="Situação agora">{d.vehicle.statusNow ?? '—'}</Fact>
                <Fact label="Situação no pedido">{d.vehicle.statusAtRequest ?? '—'}</Fact>
                <Fact label="Valor fixo">{moneyText(d.card.fixedAmount)}</Fact>
                <Fact label="Consultor">{d.consultant?.name ?? '—'}</Fact>
                <Fact label="E-mail do consultor">{d.consultant?.email ?? '—'}</Fact>
                <Fact label="Telefone do consultor">
                  {d.consultant?.phone ? formatPhone(d.consultant.phone) : '—'}
                </Fact>
              </Facts>
            </DetailSection>

            <DetailSection title="Atendimento" icon="documento">
              <FieldsForm tenant={tenant} detail={d} onChanged={invalidate} />
              {d.boleto && (
                <div className="mt-4 grid gap-1 text-sm">
                  <p className="font-semibold">Boleto do nosso número</p>
                  {d.boleto.dueDate && <p>Vencimento {formatDate(d.boleto.dueDate)}</p>}
                  {d.boleto.amount && <p>Valor {moneyText(d.boleto.amount)}</p>}
                  {d.boleto.digitableLine && (
                    <p className="font-mono break-all">{d.boleto.digitableLine}</p>
                  )}
                  {d.boleto.link && (
                    <a
                      href={d.boleto.link}
                      target="_blank"
                      rel="noreferrer"
                      className={linkTextClasses()}
                    >
                      Abrir o boleto
                    </a>
                  )}
                  {!d.boleto.digitableLine && !d.boleto.link && (
                    <p className="text-texto-suave">
                      O boleto está no cadastro, mas sem link e sem linha digitável ainda.
                    </p>
                  )}
                </div>
              )}
            </DetailSection>

            <DetailSection title="Fotos e arquivos" icon="clipe">
              {d.attachments.length === 0 ? (
                <p className="text-sm text-texto-suave">Nenhum arquivo neste pedido.</p>
              ) : (
                <ul className="grid gap-2">
                  {d.attachments.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <span>{ATTACHMENT_LABELS[a.type] ?? a.name}</span>
                      <OpenAttachment tenant={tenant} id={a.id} />
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>

            <DetailSection title="Etiquetas" icon="etiqueta">
              <LabelsEditor tenant={tenant} detail={d} onChanged={invalidate} />
            </DetailSection>

            <DetailSection title="Histórico" icon="historico">
              <ol className="grid gap-3">
                {d.history.map((h) => (
                  <li key={h.id} className="text-sm">
                    <p>
                      {h.toPhase
                        ? `${h.fromPhase ? `${RETENTION_PHASE_LABELS[h.fromPhase as RetentionPhase] ?? h.fromPhase} → ` : ''}${RETENTION_PHASE_LABELS[h.toPhase as RetentionPhase] ?? h.toPhase}`
                        : (h.details ?? '')}
                      {h.legacy && <KanbanTag>Sistema antigo</KanbanTag>}
                    </p>
                    {h.toPhase && h.details && (
                      <p className="text-texto-suave">{h.details}</p>
                    )}
                    <p className="text-xs text-texto-suave">
                      {formatInstant(h.occurredAt)} · {h.actorName}
                    </p>
                  </li>
                ))}
              </ol>
            </DetailSection>
          </DetailSections>
        </>
      )}
    </Page>
  );
}

/** Finalizar, reter, puxar, transferir, excluir — o que o servidor disse que pode. */
function Actions({
  tenant,
  detail,
  onChanged,
}: {
  tenant: string;
  detail: RetentionCardDetail;
  onChanged: () => void;
}) {
  const id = detail.card.id;
  const [error, setError] = useState<string | null>(null);
  const [retaining, setRetaining] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const run = useMutation({
    mutationFn: async (act: () => Promise<unknown>) => act(),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e) => setError(e.message),
  });
  const can = detail.can;
  if (!can.finish && !can.retain && !can.pull && !can.transfer && !can.remove)
    return null;

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap gap-2">
        {can.finish &&
          FINALS.map((f) => (
            <Button
              key={f.phase}
              variant="contorno"
              loading={run.isPending}
              onClick={() =>
                run.mutate(async () =>
                  ok(
                    await api.retention.finish({
                      params: { tenant, id },
                      body: { phase: f.phase },
                    }),
                    200,
                    'Não foi possível finalizar.',
                  ),
                )
              }
            >
              {f.label}
            </Button>
          ))}
        {can.retain && <Button onClick={() => setRetaining(true)}>Reter cliente</Button>}
        {can.pull && (
          <Button
            variant="contorno"
            loading={run.isPending}
            onClick={() =>
              run.mutate(async () =>
                ok(
                  await api.retention.pull({ params: { tenant, id }, body: {} }),
                  200,
                  'Não foi possível puxar o cartão.',
                ),
              )
            }
          >
            Puxar para mim
          </Button>
        )}
        {can.transfer && (
          <Button variant="contorno" onClick={() => setTransferring(true)}>
            Transferir
          </Button>
        )}
        {can.remove && (
          <Button
            variant="perigo"
            loading={run.isPending}
            onClick={() => {
              if (!window.confirm('Excluir este pedido de cancelamento?')) return;
              run.mutate(async () => {
                const r = await api.retention.remove({
                  params: { tenant, id },
                  body: {},
                });
                if (r.status !== 204) throw new Error('Não foi possível excluir.');
              });
            }}
          >
            Excluir
          </Button>
        )}
      </div>
      {error && <Alert tone="erro">{error}</Alert>}
      {retaining && (
        <RetainModal
          tenant={tenant}
          detail={detail}
          onClose={() => setRetaining(false)}
          onDone={() => {
            setRetaining(false);
            onChanged();
          }}
        />
      )}
      {transferring && (
        <TransferModal
          tenant={tenant}
          detail={detail}
          onClose={() => setTransferring(false)}
          onDone={() => {
            setTransferring(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** Os campos da operação — os que retido e cancelado exigem, e a observação. */
function FieldsForm({
  tenant,
  detail,
  onChanged,
}: {
  tenant: string;
  detail: RetentionCardDetail;
  onChanged: () => void;
}) {
  const [fields, setFields] = useState(detail.fields);
  useEffect(() => setFields(detail.fields), [detail.fields]);
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.retention.edit({
          params: { tenant, id: detail.card.id },
          body: fields,
        }),
        200,
        'Não foi possível salvar.',
      ),
    onSuccess: onChanged,
  });
  const readOnly = !detail.can.edit;
  const set = (k: keyof typeof fields) => (v: string) =>
    setFields((f) => ({ ...f, [k]: v === '' ? null : v }));

  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field
        label="Nosso número"
        value={fields.ourNumber ?? ''}
        readOnly={readOnly}
        onChange={(e) => set('ourNumber')(e.target.value)}
      />
      <Field
        label="Protocolo do chat"
        value={fields.chatProtocol ?? ''}
        readOnly={readOnly}
        onChange={(e) => set('chatProtocol')(e.target.value)}
      />
      <TextArea
        label="Tratativa com cliente"
        value={fields.handling ?? ''}
        readOnly={readOnly}
        onChange={(e) => set('handling')(e.target.value)}
      />
      <DateField
        label="Data de último contato"
        value={fields.lastContactOn ?? ''}
        readOnly={readOnly}
        onChange={set('lastContactOn')}
      />
      <TextArea
        label="Observação"
        value={fields.notes ?? ''}
        readOnly={readOnly}
        onChange={(e) => set('notes')(e.target.value)}
      />
      {save.isError && <Alert tone="erro">{save.error.message}</Alert>}
      {!readOnly && (
        <div>
          <Button type="submit" loading={save.isPending}>
            Salvar
          </Button>
        </div>
      )}
    </form>
  );
}

function LabelsEditor({
  tenant,
  detail,
  onChanged,
}: {
  tenant: string;
  detail: RetentionCardDetail;
  onChanged: () => void;
}) {
  const options = useQuery({
    queryKey: [...RETENTION_KEY, 'etiquetas', tenant],
    queryFn: async () =>
      ok(
        await api.retention.labels({ params: { tenant } }),
        200,
        'Não foi possível carregar.',
      ),
  });
  const save = useMutation({
    mutationFn: async (labelIds: number[]) =>
      ok(
        await api.retention.setLabels({
          params: { tenant, id: detail.card.id },
          body: { labelIds },
        }),
        200,
        'Não foi possível salvar as etiquetas.',
      ),
    onSuccess: onChanged,
  });
  return (
    <div className="grid gap-2">
      <MultiSelect
        label="Etiquetas"
        loading={options.isPending}
        options={(options.data?.items ?? []).map((l) => ({
          value: String(l.id),
          label: l.name,
        }))}
        selected={detail.card.labels.map((l) => String(l.id))}
        onChange={(ids) => save.mutate(ids.map(Number))}
        placeholder="Nenhuma"
      />
      {save.isError && <Alert tone="erro">{save.error.message}</Alert>}
    </div>
  );
}

function OpenAttachment({ tenant, id }: { tenant: string; id: number }) {
  const open = useMutation({
    mutationFn: async () =>
      ok(
        await api.retention.attachmentUrl({ params: { tenant, id } }),
        200,
        'Não foi possível abrir o arquivo.',
      ),
    onSuccess: (r) => window.open(r.url, '_blank', 'noopener'),
  });
  return (
    <Button
      size="sm"
      variant="contorno"
      loading={open.isPending}
      onClick={() => open.mutate()}
    >
      Abrir
    </Button>
  );
}

/** "Reter cliente" a partir do 72h — os quatro campos vão junto. */
function RetainModal({
  tenant,
  detail,
  onClose,
  onDone,
}: {
  tenant: string;
  detail: RetentionCardDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [fields, setFields] = useState({
    ourNumber: detail.fields.ourNumber ?? '',
    chatProtocol: detail.fields.chatProtocol ?? '',
    handling: detail.fields.handling ?? '',
    lastContactOn: detail.fields.lastContactOn ?? '',
  });
  const retain = useMutation({
    mutationFn: async () =>
      ok(
        await api.retention.retain({
          params: { tenant, id: detail.card.id },
          body: fields,
        }),
        200,
        'Não foi possível reter.',
      ),
    onSuccess: onDone,
  });
  const complete = Object.values(fields).every((v) => v.trim() !== '');
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Reter cliente">
      <div className="grid gap-3">
        <Field
          label="Nosso número"
          value={fields.ourNumber}
          onChange={(e) => setFields({ ...fields, ourNumber: e.target.value })}
        />
        <Field
          label="Protocolo do chat"
          value={fields.chatProtocol}
          onChange={(e) => setFields({ ...fields, chatProtocol: e.target.value })}
        />
        <TextArea
          label="Tratativa com cliente"
          value={fields.handling}
          onChange={(e) => setFields({ ...fields, handling: e.target.value })}
        />
        <DateField
          label="Data de último contato"
          value={fields.lastContactOn}
          layer="modal"
          onChange={(v) => setFields({ ...fields, lastContactOn: v })}
        />
        {retain.isError && <Alert tone="erro">{retain.error.message}</Alert>}
      </div>
      <ModalActions>
        <Button variant="contorno" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          loading={retain.isPending}
          disabled={!complete}
          onClick={() => retain.mutate()}
        >
          Reter
        </Button>
      </ModalActions>
    </Modal>
  );
}

function TransferModal({
  tenant,
  detail,
  onClose,
  onDone,
}: {
  tenant: string;
  detail: RetentionCardDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const operators = useQuery({
    queryKey: [...RETENTION_KEY, 'resumo-transferir', tenant],
    queryFn: async () =>
      ok(
        await api.retention.summary({ params: { tenant }, query: {} }),
        200,
        'Não foi possível carregar a escala.',
      ).operators,
  });
  const [to, setTo] = useState('');
  const [sameMember, setSameMember] = useState(false);
  const transfer = useMutation({
    mutationFn: async () =>
      ok(
        await api.retention.transfer({
          params: { tenant, id: detail.card.id },
          body: { operatorId: to, sameMember },
        }),
        200,
        'Não foi possível transferir.',
      ),
    onSuccess: onDone,
  });
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Transferir cartão">
      <div className="grid gap-3">
        <Select label="Para" value={to} onChange={(e) => setTo(e.target.value)}>
          <option value="">Escolha</option>
          {(operators.data ?? [])
            .filter((o) => o.listed && o.userId !== detail.card.operator?.id)
            .map((o) => (
              <option key={o.userId} value={o.userId}>
                {o.name}
              </option>
            ))}
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sameMember}
            onChange={(e) => setSameMember(e.target.checked)}
          />
          Levar também os outros cartões deste associado na mesma coluna
        </label>
        <p className="text-sm text-texto-suave">
          O relógio do cartão não zera ao transferir.
        </p>
        {transfer.isError && <Alert tone="erro">{transfer.error.message}</Alert>}
      </div>
      <ModalActions>
        <Button variant="contorno" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          loading={transfer.isPending}
          disabled={!to}
          onClick={() => transfer.mutate()}
        >
          Transferir
        </Button>
      </ModalActions>
    </Modal>
  );
}
